import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import ErrorState from '../components/ErrorState.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import { useAuth } from '../context/AuthContext.jsx'
import { ApiError, api } from '../lib/api.js'
import { CHUNK_BYTES, collectHistoryFiles, sha256Hex } from '../lib/spotifyExport.js'
import { plural } from '../lib/format.js'

/**
 * `/import` — Spotify listening history in, listens out (IMPORT-001, §14).
 *
 * The browser opens the zip and keeps only the streaming-history JSON, so the
 * server never holds a byte it will not parse. Progress is the job row,
 * polled while anything is running (1B-1 PLAN §13 answer 7) — the counts on
 * screen are the server's counters, never a client-side tally.
 */

const ACTIVE = new Set(['uploading', 'queued', 'parsing', 'matching'])
// Exported for the tests, which have to advance a clock past the point where
// the poll gives up. A test that hardcoded 4500 would still pass, quietly
// measuring nothing, if either of these moved.
export const POLL_MS = 1500
// A poll that misses once says nothing. Three in a row is not "still working",
// and the page has to stop implying that it is — see the poll effect.
export const MISSES_BEFORE_STALLED = 3
// How much evidence an estimate needs before it is allowed to claim anything.
// Exported for the same reason as the two above: a test that wrote 3 and 15000
// itself would keep passing while measuring a page that had stopped waiting.
export const ESTIMATE_MIN_SAMPLES = 3
export const ESTIMATE_MIN_SPAN_MS = 15_000
// And how long a count that has stopped falling may go on being spoken for.
// Deliberately the same fifteen seconds the estimate needed to earn its first
// word: the page speaks once it has that much evidence and goes quiet once it
// has that much of none. Time rather than a sample count, because at
// `POLL_MS` this already implies ten consecutive polls with nothing decided,
// and "nothing has moved for fifteen seconds" is the thing a reader can check.
export const ESTIMATE_STALE_AFTER_MS = 15_000

const STATUS_LINES = {
  uploading: 'Waiting for the rest of the upload…',
  queued: 'Waiting for the worker…',
  parsing: 'Reading your history…',
  matching: 'Matching plays against the catalog…',
  review: 'Matched — a few rows want your eyes',
  done: 'Imported',
  cancelled: 'Cancelled',
  failed: 'Failed',
}

function totals(job) {
  const matched = job.matched_auto + job.matched_likely
  const decided = matched + job.review_open + job.unmatched
  return { matched, decided, of: job.items_total, review: job.review_open }
}

const MINUTE_MS = 60_000

/**
 * A duration in words, rounded hard.
 *
 * The lookup runs against a 1 request/second throttle shared by every process,
 * so the true figure is large and its precision is false. "about 35 minutes"
 * is a claim this page can stand behind; "34 minutes 12 seconds" is not, and a
 * countdown that ticks is a promise about the next second as well as the last.
 */
function roundedDuration(ms) {
  const minutes = ms / MINUTE_MS
  if (minutes < 1) return 'under a minute'
  if (minutes < 2) return 'about a minute'
  if (minutes < 10) return `about ${Math.round(minutes)} minutes`
  if (minutes < 60) {
    const rounded = Math.round(minutes / 5) * 5
    if (rounded < 60) return `about ${rounded} minutes`
  }
  const hours = Math.round(minutes / 60)
  return hours <= 1 ? 'about an hour' : `about ${hours} hours`
}

// Approved copy (1b-import-polish §Approved copy) — the label follows the
// derived `phase`, not raw `status`, so the long lookup tail names itself.
// `estimate` is a phrase or null, and null is the ordinary case: the line says
// nothing about duration until the samples behind it support one.
function statusLine(job, estimate) {
  if (job.phase === 'materializing') {
    const remaining = `${job.materializing} remaining`
    return `Looking up well-played tracks in the catalog — ${
      estimate ? `${remaining}, ${estimate}` : remaining
    }`
  }
  return STATUS_LINES[job.status] || job.status
}

function JobCard({ job, actionError, estimate, onCancel, onDelete, onRematch, canRematch }) {
  const [confirming, setConfirming] = useState(false)
  const { matched, decided, of, review } = totals(job)
  const running = ACTIVE.has(job.status)
  const repairable =
    canRematch && !running && (job.status === 'review' || job.status === 'done') && job.unmatched > 0
  return (
    // `import-row` carries no styling above 520px. It is what the phone rule in
    // `app.css` scopes to — `.queue-actions` is shared with `/queue`, and a bare
    // `.row` is every list in the product — and what tells the 390 guard that
    // this title is authored, not user text.
    <div className="row import-row">
      <div className="row-main">
        <div className="row-title">
          {new Date(job.created_at).toLocaleDateString()} — {statusLine(job, estimate)}
        </div>
        <div className="row-sub wrap">
          {job.listens_created} {plural(job.listens_created, 'listen')} from {job.rows_seen}{' '}
          {plural(job.rows_seen, 'play')}
          {job.duplicates_skipped > 0 ? ` · ${job.duplicates_skipped} already imported` : ''}
          {/* Approved copy (Josh, 2026-09-23). `skipped_non_music` counts every
              row the parse dropped — podcasts and videos, and rows it could not
              read — so the sentence names all three rather than the field. */}
          {job.skipped_non_music > 0
            ? ` · ${job.skipped_non_music} skipped (podcasts, videos or unreadable rows)`
            : ''}
          {of > 0 && running ? ` · ${decided} of ${of} ${plural(of, 'track')} decided` : ''}
          {of > 0 && !running ? (
            <>
              {` · ${matched} matched · `}
              {review > 0 ? (
                <Link to="/import/review">{review} to review</Link>
              ) : (
                `${review} to review`
              )}
              {` · ${job.unmatched} no match`}
            </>
          ) : null}
          {review > 0 && running ? (
            <>
              {' · '}
              <Link to="/import/review">review {review}</Link>
            </>
          ) : null}
          {job.error ? ` · ${job.error}` : ''}
        </div>
        {/* What the row's own buttons answered, as opposed to `job.error`,
            which is what the import itself went wrong with. */}
        <InlineAlert>{actionError}</InlineAlert>
      </div>
      <div className="queue-actions">
        {repairable && !confirming ? (
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => onRematch(job.id)}>
            Try matching again
          </button>
        ) : null}
        {ACTIVE.has(job.status) ? (
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => onCancel(job.id)}>
            Cancel
          </button>
        ) : confirming ? (
          <>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => onDelete(job.id)}
            >
              Delete {job.listens_created} {plural(job.listens_created, 'listen')}
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => setConfirming(false)}
            >
              Keep
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={() => setConfirming(true)}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  )
}

export default function ImportPage() {
  const { user } = useAuth()
  const [found, setFound] = useState([])
  const [rematching, setRematching] = useState(null) // job id awaiting confirm
  const [rematchBusy, setRematchBusy] = useState(false)
  const [rematchError, setRematchError] = useState('')
  const [ignored, setIgnored] = useState([])
  const [pickError, setPickError] = useState('')
  const [dragging, setDragging] = useState(false)
  // { step, fileIndex, fileCount, fileName, sent, total } — `total` is every
  // file's bytes together, and `sent` counts across all of them, so the bar
  // fills once. Cleared on both the success and the error path in `start`; a
  // shape that only cleared on one would leave a bar frozen part-way forever.
  const [upload, setUpload] = useState(null)
  // The upload in flight: { controller, jobId, finalizeSent, stopped }. A ref
  // rather than state because the loop in `start` and the leave handlers read
  // it between renders — a stop has to be seen before the next chunk, not
  // after the next commit.
  const runRef = useRef(null)
  const [active, setActive] = useState(null) // the job being watched
  // A phrase like `about 20 minutes left`, or null — which is the state it
  // stays in until the lookup has been watched long enough to mean it.
  const [estimate, setEstimate] = useState(null)
  // { jobId, first, last, count, shownMs } for the catalog lookup only.
  const lookupRef = useRef(null)
  const [history, setHistory] = useState({ loading: true, items: [] })
  const [error, setError] = useState('')
  const [historyError, setHistoryError] = useState('')
  // What a row's own Cancel or Delete answered, keyed by job id — one message
  // per row, because the person pressed a button on one row.
  const [rowErrors, setRowErrors] = useState({})
  const [locked, setLocked] = useState('')
  const [stalled, setStalled] = useState(false)
  const pollRef = useRef(null)

  const loadHistory = useCallback(async (signal) => {
    try {
      const body = await api.imports({ signal })
      setHistoryError('')
      setHistory({ loading: false, items: body.items })
      const running = body.items.find((job) => ACTIVE.has(job.status))
      // Adopt a running job — and, just as importantly, let the list correct a
      // stale one. `active` is what both the banner and the poll read, so a
      // job left holding a status the server has moved past keeps the page
      // claiming work is in progress. This used to only ever *set* `active`,
      // which meant the per-job poll was the single route out of that state:
      // if it could not reach the server, nothing here ever put it right.
      setActive((current) => {
        if (running) return running
        if (!current) return null
        return body.items.find((row) => row.id === current.id) || null
      })
      return body.items
    } catch (err) {
      if (err.name === 'AbortError') return []
      if (err instanceof ApiError && err.status === 403) {
        // §13 answer 8: owner-first dogfood. The page is the door, closed.
        setLocked(err.message)
        setHistory({ loading: false, items: [] })
        return []
      }
      setHistoryError(err instanceof ApiError ? err.message : 'Your imports did not load.')
      setHistory((current) => ({ ...current, loading: false }))
      return []
    }
  }, [])

  useEffect(() => {
    document.title = 'Import listening history — Sonologue'
    const controller = new AbortController()
    loadHistory(controller.signal)
    return () => controller.abort()
  }, [loadHistory])

  /**
   * How long the catalog lookup has left, from the rate this job has achieved.
   *
   * **Only while `phase === 'materializing'`, and that is the whole design.**
   * `materializing` is `items_total` minus the four decided buckets
   * (`api/app/modules/imports/schemas.py`), and during the local match batch
   * that difference also counts still-`pending` items — which are decided in
   * milliseconds, not at one MusicBrainz request per second. A rate measured
   * across both phases is wildly pessimistic for the first minute and then
   * collapses, which is worse than saying nothing.
   *
   * No new field on `ImportJobOut`: `materializing` and the clock are enough,
   * and the parallel-safety of this pass and the backend one rests on it.
   *
   * Runs on every change to `active`, which is one per poll.
   */
  useEffect(() => {
    if (!active || active.phase !== 'materializing') {
      lookupRef.current = null
      setEstimate(null)
      return
    }
    const previous = lookupRef.current
    const sample = { t: Date.now(), remaining: active.materializing }
    if (!previous || previous.jobId !== active.id) {
      lookupRef.current = {
        jobId: active.id,
        first: sample,
        last: sample,
        count: 1,
        shownMs: null,
        lastProgressAt: sample.t,
      }
      setEstimate(null)
      return
    }
    const ring = {
      ...previous,
      last: sample,
      count: previous.count + 1,
      lastProgressAt:
        sample.remaining < previous.last.remaining ? sample.t : previous.lastProgressAt,
    }
    lookupRef.current = ring

    // The evidence has gone stale. A job can poll perfectly and decide nothing
    // — which is why `MISSES_BEFORE_STALLED` does not cover this: that counts
    // failed *polls*. Without this the page goes on advertising a duration for
    // ever off a rate nothing has supported since the count last moved.
    if (sample.t - ring.lastProgressAt >= ESTIMATE_STALE_AFTER_MS) {
      setEstimate(null)
      return
    }

    const span = ring.last.t - ring.first.t
    const done = ring.first.remaining - ring.last.remaining
    if (ring.count < ESTIMATE_MIN_SAMPLES || span < ESTIMATE_MIN_SPAN_MS || done <= 0) {
      setEstimate(null)
      return
    }
    // Clamped so the estimate never revises upward. A figure that grows reads
    // as the work going backwards and costs the page the trust the number was
    // there to buy; a coarse figure that only ever shrinks does not. `shownMs`
    // survives a stale spell on purpose: when the count starts falling again
    // the cumulative rate has been dragged down by the stall, so an unclamped
    // figure would come back *larger* than the one the page was showing before
    // it went quiet.
    const ms = Math.min((ring.last.remaining * span) / done, ring.shownMs ?? Infinity)
    ring.shownMs = ms
    setEstimate(`${roundedDuration(ms)} left`)
  }, [active])

  // The poll: while a job is active, its row on the server is the truth the
  // page repeats. Stops itself on any terminal status.
  useEffect(() => {
    if (!active || !ACTIVE.has(active.status)) return undefined
    let misses = 0
    pollRef.current = setInterval(async () => {
      try {
        const job = await api.importJob(active.id)
        misses = 0
        setStalled(false)
        setActive(job)
        setHistory((current) => ({
          ...current,
          items: current.items.map((row) => (row.id === job.id ? job : row)),
        }))
      } catch {
        // A poll that misses once says nothing; the next one answers. Three in
        // a row is a different thing, and swallowing it was how this page
        // could sit on a job the server had already finished — the counters
        // frozen at their last-known values, the label still saying work was
        // happening, and nothing anywhere to say the page had stopped being
        // able to ask. Fall back to the list, which is a different route and
        // can be answerable when the per-job one is not, and say so meanwhile.
        misses += 1
        if (misses >= MISSES_BEFORE_STALLED) {
          setStalled(true)
          loadHistory()
        }
      }
    }, POLL_MS)
    return () => clearInterval(pollRef.current)
  }, [active, loadHistory])

  async function pick(fileList) {
    setPickError('')
    setError('')
    try {
      const { found: files, ignored: skipped, empty } = await collectHistoryFiles(
        Array.from(fileList),
      )
      if (files.length === 0) {
        // Approved copy for an empty file (Josh, 2026-09-23, Q23). The server
        // would refuse one anyway; saying so here is what keeps its generic
        // validation sentence off the page.
        if (empty.length === 1) {
          setPickError(`${empty[0]} is empty, so there is nothing in it to import.`)
        } else if (empty.length > 1) {
          setPickError(
            `These files are empty, so there is nothing in them to import: ${empty.join(', ')}.`,
          )
        } else {
          setPickError(
            'No streaming history found. Upload the zip Spotify sends, or the ' +
              'Streaming_History_Audio_….json files inside it.',
          )
        }
        return
      }
      setFound(files)
      setIgnored([...skipped, ...empty.map((name) => `${name} (empty file)`)])
    } catch (err) {
      setPickError(err.message || 'That file could not be opened.')
    }
  }

  async function start() {
    setError('')
    const run = { controller: new AbortController(), jobId: null, finalizeSent: false, stopped: false }
    runRef.current = run
    const { signal } = run.controller
    try {
      // One bar over every file's bytes, so it fills once and never restarts.
      // `total` used to be a single file's length, which is why an eight-file
      // export showed the bar reach 100% eight times.
      const total = found.reduce((sum, file) => sum + file.bytes.byteLength, 0)
      const fileCount = found.length
      const manifests = []
      for (const [index, file] of found.entries()) {
        if (run.stopped) return
        // The SHA-256 pass runs over every file before the first chunk leaves,
        // and used to report nothing at all — on a large export that is a
        // silent stretch where the page looks like it has not started.
        setUpload({ step: 'preparing', fileIndex: index, fileCount, fileName: file.name, sent: 0, total })
        manifests.push({
          name: file.name,
          byte_size: file.bytes.byteLength,
          sha256: await sha256Hex(file.bytes),
        })
      }
      if (run.stopped) return
      const job = await api.createImport(manifests)
      run.jobId = job.id
      if (run.stopped) {
        // Stopped while the job was being created, when there was no id to
        // cancel yet — so the cancel is this path's to send.
        await api.cancelImport(job.id).catch(() => {})
        loadHistory()
        return
      }
      let uploaded = 0
      for (const [index, file] of found.entries()) {
        let sent = 0
        setUpload({ step: 'uploading', fileIndex: index, fileCount, fileName: file.name, sent: uploaded, total })
        while (sent < file.bytes.byteLength) {
          // Checked before every chunk, not only by the abort: a chunk whose
          // answer was already on its way back resolves anyway.
          if (run.stopped) return
          const chunk = file.bytes.slice(sent, sent + CHUNK_BYTES)
          await api.uploadImportChunk(job.id, index, sent, chunk, { signal })
          sent += chunk.byteLength
          uploaded += chunk.byteLength
          setUpload({ step: 'uploading', fileIndex: index, fileCount, fileName: file.name, sent: uploaded, total })
        }
      }
      if (run.stopped) return
      // Set before the request, not after: leaving while finalize is in flight
      // must not cancel an import that may already be queued.
      run.finalizeSent = true
      const queued = await api.finalizeImport(job.id)
      if (run.stopped) return
      setFound([])
      setIgnored([])
      setUpload(null)
      setActive(queued)
      setHistory((current) => ({
        ...current,
        items: [queued, ...current.items.filter((row) => row.id !== queued.id)],
      }))
    } catch (err) {
      if (run.stopped) return // a stop owns the page from here, error line included
      run.stopped = true
      setUpload(null)
      setError(err instanceof ApiError ? err.message : 'The upload did not go through.')
      // A job that exists and failed would otherwise sit `uploading` until the
      // server's idle timer ends it. Once finalize is sent, only its 422 says
      // for certain that the job is still uploading; anything else may have
      // landed, and the timer covers it if not. Best-effort: the error line
      // keeps the failure's own sentence.
      const refused = err instanceof ApiError && err.status === 422
      if (run.jobId && (!run.finalizeSent || refused)) {
        await api.cancelImport(run.jobId).catch(() => {})
        loadHistory()
      }
    } finally {
      if (runRef.current === run) runRef.current = null
    }
  }

  // Stops the upload in flight: no further chunk, the one in flight aborted,
  // and the job — once one exists — cancelled. `keepalive` is for `pagehide`,
  // where the request has to outlive the page.
  async function stopUpload({ keepalive = false } = {}) {
    const run = runRef.current
    if (!run || run.stopped) return
    run.stopped = true
    run.controller.abort()
    setUpload(null)
    setFound([])
    setIgnored([])
    setError('')
    if (run.jobId) {
      try {
        await api.cancelImport(run.jobId, { keepalive })
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not cancel that import just now.')
      }
    }
    loadHistory()
  }

  // Leaving mid-upload (Josh, 2026-09-23): the browser's own "Leave site?"
  // while bytes are being sent, and a cancel on the way out, so the staged
  // files go now rather than an idle window later. Both stand down once
  // finalize is sent. In-app navigation unmounts the page and removes these,
  // so the loop carries on and finishes, as it always has.
  const uploading = upload !== null
  const stopUploadRef = useRef(stopUpload)
  stopUploadRef.current = stopUpload
  useEffect(() => {
    if (!uploading) return undefined
    const sendingBytes = () => {
      const run = runRef.current
      return Boolean(run && run.jobId && !run.finalizeSent && !run.stopped)
    }
    const onBeforeUnload = (event) => {
      if (!sendingBytes()) return
      event.preventDefault()
      event.returnValue = true // Chrome and Edge before 119 read this instead
    }
    const onPageHide = () => {
      if (sendingBytes()) stopUploadRef.current({ keepalive: true })
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [uploading])

  async function cancel(jobId) {
    setRowErrors((current) => ({ ...current, [jobId]: '' }))
    try {
      const job = await api.cancelImport(jobId)
      setActive(job)
      setHistory((current) => ({
        ...current,
        items: current.items.map((row) => (row.id === job.id ? job : row)),
      }))
    } catch (err) {
      // Racing the poll is the common way here: the API answers 409 with the
      // sentence that says what to do instead, and that is what shows.
      setRowErrors((current) => ({
        ...current,
        [jobId]:
          err instanceof ApiError ? err.message : 'Could not cancel that import just now.',
      }))
    }
  }

  async function confirmRematch() {
    setRematchBusy(true)
    setRematchError('')
    try {
      await api.rematchImport(rematching)
      setRematching(null)
      // The worker flips the row back to `matching`; refresh once it has
      // had a beat, and the normal poll takes over from there.
      setTimeout(() => loadHistory(), 2000)
      setTimeout(() => loadHistory(), 6000)
    } catch (err) {
      setRematchError(err instanceof ApiError ? err.message : 'The re-match did not start.')
    } finally {
      setRematchBusy(false)
    }
  }

  async function remove(jobId) {
    setRowErrors((current) => ({ ...current, [jobId]: '' }))
    try {
      await api.deleteImport(jobId)
      if (active?.id === jobId) setActive(null)
      setHistory((current) => ({
        ...current,
        items: current.items.filter((row) => row.id !== jobId),
      }))
    } catch (err) {
      // The row stays, and stays confirming, so the pair of buttons is still
      // there to try again or to keep the listens.
      setRowErrors((current) => ({
        ...current,
        [jobId]: err instanceof ApiError ? err.message : 'That did not delete.',
      }))
    }
  }

  const uploadPct = upload && upload.total > 0 ? Math.round((upload.sent / upload.total) * 100) : 0

  if (locked) {
    return (
      <>
        <div className="section-header">
          <h1 className="title-2">Import listening history</h1>
        </div>
        <div className="empty-state">
          <h2>Not open yet</h2>
          <p>{locked}</p>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="section-header">
        <h1 className="title-2">Import listening history</h1>
      </div>

      {/* Consent copy — owner approved 2026-08-03 (§13 answer 6). */}
      <div className="alpha-note">
        <strong>Private by default.</strong> Your listening history is visible only to you: it
        never posts to your feed and never touches community stats. The files you upload are
        parsed and then deleted — Spotify includes things like IP addresses in them, and
        Sonologue keeps only when you played what. You can delete all imported listening data at
        any time.
      </div>

      <div className="card card-pad">
        <label
          className={`dropzone ${dragging ? 'active' : ''}`}
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            pick(event.dataTransfer.files)
          }}
        >
          <input
            type="file"
            multiple
            accept=".zip,.json,application/zip,application/json"
            onChange={(event) => pick(event.target.files)}
          />
          <strong>Drop your Spotify export here</strong>
          <br />
          <span className="footnote">
            The zip from Spotify&rsquo;s privacy page (&ldquo;Extended streaming history&rdquo;),
            or the JSON files inside it. Click to choose files.
          </span>
        </label>

        {pickError ? <p className="footnote mt-3">{pickError}</p> : null}

        {found.length > 0 ? (
          <div className="mt-4">
            <ul className="hairline-list">
              {found.map((file) => (
                <li key={file.name} className="row">
                  <span className="row-main">
                    <span className="row-title">{file.name}</span>
                    <span className="row-sub">{Math.ceil(file.bytes.byteLength / 1024)} KB</span>
                  </span>
                </li>
              ))}
            </ul>
            {ignored.length > 0 ? (
              <p className="footnote mt-2">Ignored: {ignored.join(', ')}</p>
            ) : null}
            {upload ? (
              <div className="mt-3">
                <div
                  className="progress-track"
                  role="progressbar"
                  aria-label="Upload progress"
                  aria-valuenow={uploadPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="progress-fill" style={{ width: `${uploadPct}%` }} />
                </div>
                <p className="footnote mt-2 upload-caption">
                  <span>
                    {upload.step === 'preparing' ? 'Checking' : 'Uploading'} file{' '}
                    {upload.fileIndex + 1} of {upload.fileCount} — {uploadPct}%
                  </span>
                  <span className="upload-file">{upload.fileName}</span>
                </p>
                <button type="button" className="btn btn-quiet btn-sm mt-2" onClick={() => stopUpload()}>
                  Cancel upload
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-primary mt-3" onClick={start}>
                Import {found.length} {found.length === 1 ? 'file' : 'files'}
              </button>
            )}
          </div>
        ) : null}

        {error ? <p className="footnote mt-3">{error}</p> : null}
      </div>

      {active && ACTIVE.has(active.status) ? (
        <div className="card card-pad mt-4" aria-live="polite">
          <p className="mb-0">
            <strong>{statusLine(active, estimate)}</strong>
            {active.rows_seen > 0 ? (
              <span className="footnote">
                {' '}
                — {active.listens_created} {plural(active.listens_created, 'listen')} from{' '}
                {active.rows_seen} {plural(active.rows_seen, 'play')} so far
              </span>
            ) : null}
          </p>
          {active.items_total > 0 ? (
            <p className="footnote mt-2 mb-0">
              {totals(active).decided} of {active.items_total}{' '}
              {plural(active.items_total, 'track')} decided
            </p>
          ) : null}
          {/* Not while `uploading`: the browser is the thing sending the bytes
              then, so closing the tab really would stop it. Once the server
              has the files the work is the worker's, and the long lookup tail
              is exactly where somebody sits waiting for a page they do not
              need to keep open. */}
          {active.status !== 'uploading' && !stalled ? (
            <p className="footnote mt-2 mb-0">
              You can close this tab — the import carries on without it.
            </p>
          ) : null}
          {stalled ? (
            <p className="footnote mt-2 mb-0">
              These numbers have stopped updating — this page cannot reach the server. Your import
              is unaffected and carries on without this page open.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="section-header mt-5">
        <h2 className="title-3">Past imports</h2>
      </div>
      <div className="card">
        {historyError ? (
          <ErrorState
            title="Your imports did not load"
            message={historyError}
            onRetry={() => loadHistory()}
          />
        ) : history.loading ? (
          <div className="card-pad">
            <p className="footnote mb-0">Loading…</p>
          </div>
        ) : history.items.length === 0 ? (
          <div className="empty-state">
            <h2>Nothing imported yet</h2>
            <p>
              Request your extended streaming history from Spotify&rsquo;s privacy settings —
              it usually arrives within a couple of days — then drop the zip above. Your unrated
              queue at <Link to="/queue">/queue</Link> fills from it.
            </p>
          </div>
        ) : (
          <div className="hairline-list">
            {history.items.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                actionError={rowErrors[job.id]}
                // The samples behind the estimate are the watched job's, so
                // only that row gets to name a duration.
                estimate={job.id === active?.id ? estimate : null}
                onCancel={cancel}
                onDelete={remove}
                onRematch={(jobId) => {
                  setRematchError('')
                  setRematching(jobId)
                }}
                canRematch={Boolean(user?.is_admin)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={rematching !== null}
        title="Try matching again"
        tone="primary"
        confirmLabel="Try matching again"
        busy={rematchBusy}
        error={rematchError}
        onConfirm={confirmRematch}
        onClose={() => setRematching(null)}
      >
        <p>
          Re-run matching for unmatched tracks? Well-played tracks use the catalog lookup
          budget; a large job may take about half an hour.
        </p>
      </ConfirmDialog>
    </>
  )
}
